/** 시간별 운동 계약. 전체 개요가 가리키는 세대와 같은 세대의 상세 바이트만 읽는다. */
import { fetchSignedJson, fetchSignedTextRange, isHttpClientError } from "@/http";

export type MotionPoint = { t: number; v: number | null };
export type MotionSeries = {
  channel: string;
  metric: string;
  unit: string | null;
  points: readonly MotionPoint[];
};
export type MotionRange = {
  start_seconds: number;
  end_seconds: number;
  offset: number;
  length: number;
};
export type MotionOverview = {
  schema_version: 3;
  generation: string;
  source_fingerprint: string;
  calculation_signature: string;
  calculator_version: "grid_resample_motion_v1";
  gap_multiple: 5;
  grid_seconds: 0.05;
  start_seconds: number;
  end_seconds: number;
  series: readonly MotionSeries[];
  ranges: readonly MotionRange[];
  skipped_channels: readonly { channel: string; reason: string }[];
};
export type MotionWindow = { start: number; end: number };
const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const finite = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

function parseSeries(raw: unknown): MotionSeries[] {
  if (!Array.isArray(raw)) throw new Error("운동 시계열 형식이 올바르지 않습니다.");
  return raw.map((s: unknown) => {
    if (
      !record(s) ||
      typeof s.channel !== "string" ||
      typeof s.metric !== "string" ||
      (s.unit !== null && typeof s.unit !== "string") ||
      !Array.isArray(s.points)
    )
      throw new Error("운동 채널 형식이 올바르지 않습니다.");
    let previous = -Infinity;
    const points = s.points.map((p: unknown) => {
      if (!record(p) || !finite(p.t) || (p.v !== null && !finite(p.v)) || p.t < previous)
        throw new Error("운동 표본의 시간이나 값이 올바르지 않습니다.");
      previous = p.t;
      return { t: p.t, v: p.v };
    });
    return { channel: s.channel, metric: s.metric, unit: s.unit, points };
  });
}

export function parseMotionOverview(raw: unknown): MotionOverview {
  if (
    !record(raw) ||
    raw.schema_version !== 3 ||
    typeof raw.generation !== "string" ||
    typeof raw.source_fingerprint !== "string" ||
    typeof raw.calculation_signature !== "string" ||
    raw.calculator_version !== "grid_resample_motion_v1" ||
    raw.gap_multiple !== 5 ||
    raw.grid_seconds !== 0.05 ||
    !finite(raw.start_seconds) ||
    !finite(raw.end_seconds) ||
    raw.end_seconds < raw.start_seconds ||
    !Array.isArray(raw.ranges) ||
    !Array.isArray(raw.skipped_channels)
  )
    throw new Error("운동 데이터 버전이나 시간 범위가 올바르지 않습니다.");
  let nextOffset = 0;
  let previousEnd = -Infinity;
  const ranges = raw.ranges.map((r: unknown) => {
    if (
      !record(r) ||
      !finite(r.start_seconds) ||
      !finite(r.end_seconds) ||
      r.end_seconds <= r.start_seconds ||
      r.start_seconds < previousEnd ||
      !finite(r.offset) ||
      r.offset !== nextOffset ||
      !Number.isSafeInteger(r.offset) ||
      !finite(r.length) ||
      !Number.isSafeInteger(r.length) ||
      r.length <= 0
    )
      throw new Error("상세 데이터 색인이 올바르지 않습니다.");
    nextOffset += r.length;
    previousEnd = r.end_seconds;
    return {
      start_seconds: r.start_seconds,
      end_seconds: r.end_seconds,
      offset: r.offset,
      length: r.length,
    };
  });
  const skipped = raw.skipped_channels.map((s: unknown) => {
    if (!record(s) || typeof s.channel !== "string" || typeof s.reason !== "string")
      throw new Error("미산출 채널 형식이 올바르지 않습니다.");
    return { channel: s.channel, reason: s.reason };
  });
  return {
    schema_version: 3,
    generation: raw.generation,
    source_fingerprint: raw.source_fingerprint,
    calculation_signature: raw.calculation_signature,
    calculator_version: raw.calculator_version,
    gap_multiple: raw.gap_multiple,
    grid_seconds: raw.grid_seconds,
    start_seconds: raw.start_seconds,
    end_seconds: raw.end_seconds,
    series: parseSeries(raw.series),
    ranges,
    skipped_channels: skipped,
  };
}

export async function loadMotionOverview(
  url: string,
  signal: AbortSignal,
): Promise<MotionOverview | null> {
  try {
    return parseMotionOverview(await fetchSignedJson<unknown>(url, signal));
  } catch (error) {
    if (isHttpClientError(error) && error.status === 404) return null;
    throw error;
  }
}

/**
 * 재생 바가 보고한 창을 개요 범위로 **자른다**.
 *
 * 바의 창은 영상 길이에 끝 여백 1초를 더한 데까지 간다. 영상 길이와 개요의 기록 범위도
 * 항상 같지는 않다. 폭을 유지하고 창을 안으로 옮기는 방식은 여기서 틀린다.
 * 창 끝이 개요 끝을 넘으면 창이 폭을 유지하려고 왼쪽으로 이동한다.
 * 그러면 **바가 보여주지 않는 구간을 차트가 그린다.**
 * 목적은 두 시간축을 한 화면에서 맞추는 것이다. 그래서 교집합을 쓴다.
 *
 * 100% 에서는 바의 창이 개요 범위 전체를 덮는다. 그래서 결과는 개요 전체다.
 * 개요가 영상보다 1초 넘게 길면 그 끝부분은 바의 창 밖에 있어 이 보기에 보이지 않는다.
 * 실측에서 이 경우가 나오면 그때 예외를 추가한다. 지금 예외를 허용하면 끝 구간을
 * 확대했을 때 창이 보이는 범위보다 넓어진다.
 */
export function motionViewportWindow(
  viewport: { startSeconds: number; endSeconds: number },
  overview: Pick<MotionOverview, "start_seconds" | "end_seconds">,
): MotionWindow {
  const start = Math.max(
    overview.start_seconds,
    Math.min(overview.end_seconds, viewport.startSeconds),
  );
  return { start, end: Math.max(start, Math.min(overview.end_seconds, viewport.endSeconds)) };
}

export async function loadMotionDetail(
  url: string,
  overview: MotionOverview,
  ranges: readonly MotionRange[],
  signal: AbortSignal,
): Promise<readonly MotionSeries[]> {
  if (ranges.length === 0) return [];
  const first = ranges[0]!;
  const last = ranges[ranges.length - 1]!;
  // 전체 상세의 인접 줄을 Range GET 하나로 묶는다. 그래서 RTT 가 늘지 않는다.
  const text = await fetchSignedTextRange(
    url,
    first.offset,
    last.offset + last.length - first.offset,
    signal,
  );
  const lines = text.trim().split("\n");
  if (lines.length !== ranges.length) throw new Error("상세 데이터 범위가 요청과 다릅니다.");
  const combined = new Map<string, MotionSeries & { points: MotionPoint[] }>();
  lines.forEach((line, index) => {
    const raw: unknown = JSON.parse(line);
    const range = ranges[index]!;
    if (
      !record(raw) ||
      raw.schema_version !== 3 ||
      raw.generation !== overview.generation ||
      raw.source_fingerprint !== overview.source_fingerprint ||
      raw.calculation_signature !== overview.calculation_signature ||
      raw.calculator_version !== overview.calculator_version ||
      raw.gap_multiple !== overview.gap_multiple ||
      raw.grid_seconds !== overview.grid_seconds ||
      raw.start_seconds !== range.start_seconds ||
      raw.end_seconds !== range.end_seconds
    )
      throw new Error("진단 데이터가 변경되었습니다. 상세를 새로고침해 주세요.");
    for (const series of parseSeries(raw.series)) {
      const key = JSON.stringify([series.channel, series.metric]);
      const previous = combined.get(key);
      if (previous !== undefined && previous.unit !== series.unit)
        throw new Error("운동 상세의 단위가 구간마다 다릅니다.");
      // 겹친 문맥만 제거한다. 시각으로 합치면 같은 시각의 좌우 값과 null 단절이 사라진다.
      // 첫 줄의 앞 문맥과 마지막 줄의 뒤 문맥은 표시 창의 경계를 그리는 데 필요하다.
      const points = series.points.filter(
        (point) =>
          (point.t >= range.start_seconds && point.t < range.end_seconds) ||
          (index === 0 && point.t < range.start_seconds) ||
          (index === lines.length - 1 && point.t >= range.end_seconds),
      );
      // 전체 에피소드에서 청크마다 누적 배열을 복사하면 비용이 표본 수의 제곱으로 는다.
      if (previous) {
        for (const point of points) previous.points.push(point);
      } else {
        combined.set(key, { ...series, points });
      }
    }
  });
  return [...combined.values()];
}
